import { type ReactElement, type ReactNode, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, VStack } from '@chakra-ui/react';

import { type AppToastStatus, AppToast } from './app-toast';

export type AppToastParams = {
	title: string;
	description?: string;
	persistent?: boolean;
};

export type AppToastLoadingParams = {
	title: string;
	description?: string;
	timeout?: number;
	timeoutMessage?: string;
};

export type AppToastControl = {
	success: (params: AppToastParams) => void;
	error: (params: AppToastParams) => void;
	warning: (params: AppToastParams) => void;
	close: () => void;
};

export type AppToastContextValue = {
	loading: (params: AppToastLoadingParams) => AppToastControl;
	success: (params: AppToastParams) => void;
	error: (params: AppToastParams) => void;
	warning: (params: AppToastParams) => void;
	info: (params: AppToastParams) => void;
};

type ToastEntry = {
	id: number;
	status: AppToastStatus;
	title: string;
	description?: string;
	persistent?: boolean;
};

export type AppToastProviderProps = {
	children: ReactNode;
};

const DEFAULT_TIMEOUT = 60000;
const DEFAULT_TIMEOUT_MESSAGE = 'Operation timed out. Please try again.';
const AUTO_DISMISS_MS = 4000;

let nextId = 0;

export const AppToastContext = createContext<AppToastContextValue | null>(null);

export const AppToastProvider = (props: AppToastProviderProps): ReactElement => {
	const { children } = props;
	const [toasts, setToasts] = useState<Array<ToastEntry>>([]);
	const timeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
	const resolvedRef = useRef<Set<number>>(new Set());
	const autoDismissRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

	const removeToast = useCallback((id: number) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
		resolvedRef.current.delete(id);
		const timeout = timeoutsRef.current.get(id);
		if (timeout) {
			clearTimeout(timeout);
			timeoutsRef.current.delete(id);
		}
		const autoDismiss = autoDismissRef.current.get(id);
		if (autoDismiss) {
			clearTimeout(autoDismiss);
			autoDismissRef.current.delete(id);
		}
	}, []);

	const addToast = useCallback((entry: ToastEntry): void => {
		setToasts((prev) => [...prev, entry]);
	}, []);

	const updateToast = useCallback((id: number, updates: Partial<ToastEntry>): void => {
		setToasts((prev) => prev.map((t) => t.id === id ? { ...t, ...updates } : t));
	}, []);

	const loading = useCallback((params: AppToastLoadingParams): AppToastControl => {
		const { title, description, timeout = DEFAULT_TIMEOUT, timeoutMessage = DEFAULT_TIMEOUT_MESSAGE } = params;
		const id = nextId++;

		addToast({ id, status: 'loading', title, description });

		// A timeout is provisional, not terminal — marking it resolved would discard the real outcome when it arrives late.
		const timeoutId = setTimeout(() => {
			if (!resolvedRef.current.has(id)) {
				updateToast(id, { status: 'error', title: timeoutMessage, description: undefined, persistent: true });
				timeoutsRef.current.delete(id);
			}
		}, timeout);

		timeoutsRef.current.set(id, timeoutId);

		const resolve = (status: AppToastStatus, resolveParams: AppToastParams): void => {
			if (resolvedRef.current.has(id)) return;
			resolvedRef.current.add(id);
			const existingTimeout = timeoutsRef.current.get(id);
			if (existingTimeout) {
				clearTimeout(existingTimeout);
				timeoutsRef.current.delete(id);
			}
			const persistent = resolveParams.persistent || status === 'error' || status === 'warning';
			updateToast(id, { status, title: resolveParams.title, description: resolveParams.description, persistent });
		};

		return {
			success: (p: AppToastParams) => resolve('success', p),
			error: (p: AppToastParams) => resolve('error', p),
			warning: (p: AppToastParams) => resolve('warning', p),
			close: () => removeToast(id),
		};
	}, [addToast, updateToast, removeToast]);

	const oneShot = useCallback((status: AppToastStatus, params: AppToastParams): void => {
		const id = nextId++;
		const persistent = params.persistent || status === 'error' || status === 'warning';
		addToast({ id, status, title: params.title, description: params.description, persistent });
	}, [addToast]);

	const success = useCallback((params: AppToastParams): void => { oneShot('success', params); }, [oneShot]);
	const error = useCallback((params: AppToastParams): void => { oneShot('error', params); }, [oneShot]);
	const warning = useCallback((params: AppToastParams): void => { oneShot('warning', params); }, [oneShot]);
	const info = useCallback((params: AppToastParams): void => { oneShot('info', params); }, [oneShot]);

	const contextValue = useMemo<AppToastContextValue>(() => ({ loading, success, error, warning, info }), [loading, success, error, warning, info]);

	useEffect(() => {
		for (const toast of toasts) {
			if (toast.status === 'loading' || toast.persistent || autoDismissRef.current.has(toast.id)) continue;
			const timerId = setTimeout(() => {
				autoDismissRef.current.delete(toast.id);
				removeToast(toast.id);
			}, AUTO_DISMISS_MS);
			autoDismissRef.current.set(toast.id, timerId);
		}
	}, [toasts, removeToast]);

	return (
		<AppToastContext.Provider value={contextValue}>
			{children}
			{toasts.length > 0 && (
				<Box
					position="fixed"
					bottom={6}
					left="50%"
					transform="translateX(-50%)"
					zIndex={1701}
					pointerEvents="auto"
				>
					<VStack spacing={3} align="center">
						{toasts.map((toast) => (
							<AppToast
								key={toast.id}
								status={toast.status}
								title={toast.title}
								description={toast.description}
								onClose={() => removeToast(toast.id)}
							/>
						))}
					</VStack>
				</Box>
			)}
		</AppToastContext.Provider>
	);
};
